import { ActivityIndicator, ScrollView, StyleSheet, Text, TextInput, TouchableOpacity, View } from "react-native";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigation } from "@react-navigation/native";
import type { NativeStackNavigationProp } from "@react-navigation/native-stack";
import { WorkspaceHeader } from "../components/chatNewInterface/interfaceComponents/workspaceHeader/WorkspaceHeader";
import TabNavigator from "../components/common/general/TabNavigator/TabNavigator";
import { useAppConfig } from "../AppConfigProvider";
import CompanionILECardList from "../components/chat-companion/CompanionILECardList/CompanionILECardList";
import CompanionLayout from "../layouts/CompanionLayout";
import type { CompanionILECardProps } from "../components/chat-companion/CompanionILECard/CompanionILECard";
import type { Objective } from "../components/chat-companion/CompanionILECard/CompanionILECard";
import type { RootStackParamList } from "../navigation/navigation.types";
import CustomText from "../components/common/general/CustomText/CustomText";
import { colors, spacing, radii, typographyScale } from "../styles/design-system";
import {
  useDeleteIleMutation,
  useGetAllUserIlEsQuery,
  GetAllUserIlEsDocument,
  useResetAiTutorMutation,
  useCreateIleFromTopicMutation,
  useGetIleCreationJobStatusQuery,
  useGetPendingIleCreationJobQuery,
} from "@axio/graphql/generated/graphql";
import { AxioActionModal } from "../components/axio-ds/Modal/AxioModal";
import { useAuth } from "../hooks/useAuth";

const stripHtmlTags = (value: string) => value.replace(/<[^>]*>/g, "").trim();

/** ILE item as returned by getAllUserILEs(completionStatus) – activityStatus and objectives[].status included */
type UserILEItem = {
  id: string;
  title: string;
  description?: string | null;
  created?: unknown;
  lastModified?: unknown;
  thumbnail?: string | null;
  activityStatus?: string;
  objectives?: Array<{ id?: string; objective?: string | null; sequence?: number | null; status?: string | null }>;
  courseObjectives?: Array<{
    id?: string;
    objective?: string | null;
    sequence?: number | null;
    status?: string | null;
  }>;
};

const CompanionActivityView = () => {
  const { t } = useTranslation();
  const { primaryColor } = useAppConfig();
  const [activeTab, setActiveTab] = useState("active");
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [formTopic, setFormTopic] = useState("");
  const [formDuration, setFormDuration] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const [pendingJobId, setPendingJobId] = useState<string | null>(null);
  const resolvedJobRef = useRef<string | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedActivityForDelete, setSelectedActivityForDelete] = useState<{ id: string; title: string } | null>(
    null
  );
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const { userInfo } = useAuth();

  const { data: pendingJobData } = useGetPendingIleCreationJobQuery({ fetchPolicy: "network-only" });

  useEffect(() => {
    const serverJobId = pendingJobData?.getPendingIleCreationJob?.jobId;
    if (serverJobId && !pendingJobId && serverJobId !== resolvedJobRef.current) {
      setPendingJobId(serverJobId);
      setShowCreateForm(true);
    }
  }, [pendingJobData, pendingJobId]);

  const {
    data: dataActive,
    loading: loadingActive,
    error: errorActive,
    refetch: refetchActive,
  } = useGetAllUserIlEsQuery({
    variables: { completionStatus: "active" } as never,
    fetchPolicy: "network-only",
  });
  const {
    data: dataCompleted,
    loading: loadingCompleted,
    error: errorCompleted,
  } = useGetAllUserIlEsQuery({
    variables: { completionStatus: "completed" } as never,
    fetchPolicy: "network-only",
  });

  const [deleteIle, { loading: deletingIle }] = useDeleteIleMutation({
    refetchQueries: [
      { query: GetAllUserIlEsDocument, variables: { completionStatus: "active" } as never },
      { query: GetAllUserIlEsDocument, variables: { completionStatus: "completed" } as never },
    ],
  });
  const [resetAiTutor] = useResetAiTutorMutation({
    refetchQueries: [
      { query: GetAllUserIlEsDocument, variables: { completionStatus: "active" } as never },
      { query: GetAllUserIlEsDocument, variables: { completionStatus: "completed" } as never },
    ],
  });

  const [createIleFromTopic, { loading: submittingJob }] = useCreateIleFromTopicMutation();

  const {
    data: jobStatusData,
    startPolling,
    stopPolling,
  } = useGetIleCreationJobStatusQuery({
    variables: { jobId: pendingJobId ?? "" },
    skip: !pendingJobId,
    fetchPolicy: "network-only",
  });

  useEffect(() => {
    if (!pendingJobId) return;
    startPolling(3000);
    return () => {
      stopPolling();
    };
  }, [pendingJobId, startPolling, stopPolling]);

  useEffect(() => {
    if (!pendingJobId || !jobStatusData?.getIleCreationJobStatus) return;

    const { status, result } = jobStatusData.getIleCreationJobStatus;

    if (status === "completed" && result?.id) {
      stopPolling();
      resolvedJobRef.current = pendingJobId;
      setPendingJobId(null);
      setShowCreateForm(false);
      setFormTopic("");
      setFormDuration("");
      void refetchActive();
    } else if (status === "completed" || status === "failed" || status === "not_found") {
      stopPolling();
      resolvedJobRef.current = pendingJobId;
      setPendingJobId(null);
      if (status === "not_found") {
        setShowCreateForm(false);
      } else {
        setFormError(t("Activity generation failed. Please try again."));
      }
    }
  }, [jobStatusData, pendingJobId, refetchActive, stopPolling, t]);

  const isGenerating = !!pendingJobId;

  const handleCreateActivity = useCallback(() => {
    if (isGenerating) return;
    setShowCreateForm(true);
  }, [isGenerating]);

  const handleCancelCreate = useCallback(() => {
    if (isGenerating) return;
    setShowCreateForm(false);
    setFormTopic("");
    setFormDuration("");
    setFormError(null);
  }, [isGenerating]);

  const handleSubmitCreate = useCallback(async () => {
    const topic = formTopic.trim();
    if (!topic) {
      setFormError(t("Please enter a topic."));
      return;
    }
    setFormError(null);

    const duration = formDuration.trim();
    const context = duration ? `Duration: ${duration}` : undefined;

    try {
      const { data } = await createIleFromTopic({ variables: { title: topic, context } });
      const result = data?.createILEFromTopic;
      if (result?.success && result.jobId) {
        setPendingJobId(result.jobId);
      } else {
        setFormError(result?.message ?? t("Failed to start activity creation."));
      }
    } catch {
      setFormError(t("Failed to create learning activity. Please try again."));
    }
  }, [createIleFromTopic, formDuration, formTopic, t]);

  const openDeleteModal = useCallback((id: string, title: string) => {
    setSelectedActivityForDelete({ id, title });
    setDeleteModalOpen(true);
  }, []);

  const closeDeleteModal = useCallback(() => {
    setDeleteModalOpen(false);
    setSelectedActivityForDelete(null);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!selectedActivityForDelete?.id) {
      return;
    }
    try {
      await deleteIle({ variables: { id: selectedActivityForDelete.id } });
    } finally {
      closeDeleteModal();
    }
  }, [closeDeleteModal, deleteIle, selectedActivityForDelete?.id]);

  const handleResetProgress = useCallback(
    async (ileId: string) => {
      if (!userInfo.userId) {
        return;
      }
      await resetAiTutor({
        variables: {
          input: {
            id: ileId,
            userId: userInfo.userId,
          },
        },
      });
    },
    [resetAiTutor, userInfo.userId]
  );

  const ilesToActivities = useCallback(
    (iles: UserILEItem[]): CompanionILECardProps[] =>
      iles.reduce<CompanionILECardProps[]>((acc, ile) => {
        if (!ile?.id || !ile.title) return acc;
        const rawObjectives = ile.objectives ?? ile.courseObjectives ?? [];
        const objectives: Objective[] = rawObjectives.map((obj, index) => {
          const rawStatus = obj?.status?.trim().toLowerCase();
          return {
            id: obj?.id ?? `${ile.id}-${index}`,
            objective: obj?.objective ?? "",
            sequence: obj?.sequence ?? index + 1,
            status: rawStatus === "completed" ? "completed" : undefined,
          };
        });
        const status: CompanionILECardProps["status"] =
          ile.activityStatus === "completed"
            ? "completed"
            : ile.activityStatus === "in_progress"
              ? "in_progress"
              : "not_started";
        const statusLabel =
          status === "completed" ? "Completed" : status === "in_progress" ? "In progress" : "Not started";
        acc.push({
          id: ile.id,
          title: ile.title,
          created: (ile.created as string) ?? (ile.lastModified as string) ?? undefined,
          description: ile.description ? stripHtmlTags(ile.description) : undefined,
          objectives,
          status,
          accessibilityLabel: `${ile.title}, Interactive Learning Experience, ${statusLabel}`,
          onResetProgress: () => {
            void handleResetProgress(ile.id);
          },
          onDeleteActivity: () => openDeleteModal(ile.id, ile.title),
        });
        return acc;
      }, []),
    [handleResetProgress, openDeleteModal]
  );

  const activitiesActive = useMemo(() => {
    const raw = (dataActive?.getAllUserILEs as { data?: UserILEItem[] } | undefined)?.data ?? [];
    return ilesToActivities(raw);
  }, [dataActive?.getAllUserILEs, ilesToActivities]);

  const activitiesCompleted = useMemo(() => {
    const raw = (dataCompleted?.getAllUserILEs as { data?: UserILEItem[] } | undefined)?.data ?? [];
    return ilesToActivities(raw);
  }, [dataCompleted?.getAllUserILEs, ilesToActivities]);

  const activitiesWithNavigationActive = useMemo(
    () =>
      activitiesActive.map((a) => ({
        ...a,
        onPress: () => navigation.navigate("Interactive Learning Environment", { activityId: a.id }),
      })),
    [activitiesActive, navigation]
  );
  const activitiesWithNavigationCompleted = useMemo(
    () =>
      activitiesCompleted.map((a) => ({
        ...a,
        onPress: () => navigation.navigate("Interactive Learning Environment", { activityId: a.id }),
      })),
    [activitiesCompleted, navigation]
  );

  const tabs = useMemo(
    () => [
      { key: "active", title: `${t("Active")} (${activitiesActive.length})` },
      { key: "completed", title: `${t("Completed")} (${activitiesCompleted.length})` },
    ],
    [t, activitiesActive.length, activitiesCompleted.length]
  );

  const displayedActivities =
    activeTab === "active" ? activitiesWithNavigationActive : activitiesWithNavigationCompleted;
  const loading = activeTab === "active" ? loadingActive : loadingCompleted;
  const error = errorActive ?? errorCompleted;

  return (
    <CompanionLayout>
      <View style={styles.container}>
        <WorkspaceHeader title={t("Learning Activities")} headingLevel={1} />
        <View style={styles.tabWrapper}>
          {!showCreateForm && (
            <TabNavigator
              tabData={tabs}
              activeTab={activeTab}
              onTabPress={(key) => {
                setActiveTab(key);
              }}
              activeTabColor={primaryColor}
              useTranslationText={false}
            />
          )}
          <TouchableOpacity
            style={[styles.createButton, { backgroundColor: primaryColor }, isGenerating && styles.buttonDisabled]}
            onPress={handleCreateActivity}
            disabled={isGenerating}>
            <Text style={styles.createButtonText}>
              {isGenerating ? t("Generating...") : t("Create Learning Activity")}
            </Text>
          </TouchableOpacity>
        </View>
        <View style={styles.tabDivider} />
        {showCreateForm ? (
          <ScrollView style={styles.scrollView}>
            <View style={styles.formContainer}>
              <View style={styles.formCard}>
                {isGenerating ? (
                  <View style={styles.generatingContainer}>
                    <ActivityIndicator size="large" color={primaryColor} />
                    <CustomText
                      text={t("Generating your learning activity...")}
                      useTranslationText={false}
                      style={styles.generatingTitle}
                    />
                    <CustomText
                      text={t(
                        "This may take a moment. You can leave this page — your activity will appear in the list once it's ready."
                      )}
                      useTranslationText={false}
                      style={styles.generatingSubtitle}
                    />
                  </View>
                ) : (
                  <>
                    <CustomText
                      text={t("Create a new learning activity")}
                      useTranslationText={false}
                      style={styles.formTitle}
                    />
                    <CustomText
                      text={t("Tell us what you'd like to learn and we'll generate an interactive activity for you.")}
                      useTranslationText={false}
                      style={styles.formSubtitle}
                    />

                    <View style={styles.fieldGroup}>
                      <Text style={styles.fieldLabel}>{t("What do you want to learn?")}</Text>
                      <TextInput
                        style={[
                          styles.input,
                          styles.inputMultiline,
                          formError && !formTopic.trim() && styles.inputError,
                        ]}
                        value={formTopic}
                        onChangeText={setFormTopic}
                        placeholder={t(
                          "Describe what you'd like to learn, e.g. How to effectively manage time and prioritize tasks to stay productive"
                        )}
                        placeholderTextColor={colors.grey[400]}
                        accessibilityLabel={t("Learning topic description")}
                        editable={!submittingJob}
                        multiline
                        numberOfLines={3}
                        textAlignVertical="top"
                      />
                    </View>

                    <View style={styles.fieldGroup}>
                      <Text style={styles.fieldLabel}>{t("How long should it be?")}</Text>
                      <TextInput
                        style={styles.input}
                        value={formDuration}
                        onChangeText={setFormDuration}
                        placeholder={t("e.g. 30 minutes, 1 hour")}
                        placeholderTextColor={colors.grey[400]}
                        accessibilityLabel={t("Activity duration")}
                        editable={!submittingJob}
                      />
                    </View>

                    {formError && (
                      <CustomText text={formError} useTranslationText={false} style={styles.formErrorText} />
                    )}

                    <View style={styles.formActions}>
                      <TouchableOpacity
                        style={styles.cancelButton}
                        onPress={handleCancelCreate}
                        disabled={submittingJob}
                        accessibilityRole="button"
                        accessibilityLabel={t("Cancel")}>
                        <Text style={styles.cancelButtonText}>{t("Cancel")}</Text>
                      </TouchableOpacity>
                      <TouchableOpacity
                        style={[
                          styles.submitButton,
                          { backgroundColor: primaryColor },
                          submittingJob && styles.buttonDisabled,
                        ]}
                        onPress={() => {
                          void handleSubmitCreate();
                        }}
                        disabled={submittingJob}
                        accessibilityRole="button"
                        accessibilityLabel={t("Create activity")}>
                        {submittingJob ? (
                          <ActivityIndicator size="small" color="#ffffff" />
                        ) : (
                          <Text style={styles.submitButtonText}>{t("Create Activity")}</Text>
                        )}
                      </TouchableOpacity>
                    </View>
                  </>
                )}
              </View>
            </View>
          </ScrollView>
        ) : (
          <ScrollView style={styles.scrollView}>
            <View style={styles.contentContainer}>
              {loading ? (
                <View style={styles.stateContainer}>
                  <ActivityIndicator accessibilityLabel="Loading learning activities" color={primaryColor} />
                </View>
              ) : displayedActivities.length > 0 ? (
                <CompanionILECardList activities={displayedActivities} scrollable />
              ) : (
                <View style={styles.stateContainer}>
                  <CustomText
                    text={error ? t("Could not load learning activities right now.") : t("No learning activities yet.")}
                    size="m"
                    useTranslationText={false}
                    style={styles.stateText}
                  />
                </View>
              )}
            </View>
          </ScrollView>
        )}
      </View>
      <AxioActionModal
        visible={deleteModalOpen}
        onRequestClose={closeDeleteModal}
        title={t("Delete learning activity")}
        primaryActionLabel={deletingIle ? t("Deleting...") : t("Delete")}
        onPrimaryAction={() => {
          void handleDeleteConfirm();
        }}
        secondaryActionLabel={t("Cancel")}
        onSecondaryAction={closeDeleteModal}>
        <View style={styles.deleteModalContent}>
          <Text style={styles.deleteModalText}>
            {t(
              `Are you sure you want to delete "${selectedActivityForDelete?.title ?? ""}"? This action cannot be undone.`
            )}
          </Text>
        </View>
      </AxioActionModal>
    </CompanionLayout>
  );
};

const styles = StyleSheet.create({
  container: {
    height: "100%",
    display: "flex",
    // @ts-expect-error - works on web
    maxHeight: "100vh",
    overflow: "hidden",
    backgroundColor: "#f9fafb",
  },
  tabWrapper: {
    marginTop: 24,
    width: "100%",
    maxWidth: 1400,
    marginHorizontal: "auto",
    paddingHorizontal: 16,
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  contentContainer: {
    flex: 1,
    width: "100%",
    maxWidth: 1400,
    marginHorizontal: "auto",
  },
  tabDivider: {
    height: 1,
    backgroundColor: "#eaecf0",
    marginHorizontal: 16,
  },
  scrollView: {
    flex: 1,
  },
  stateContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    paddingVertical: 32,
  },
  stateText: {
    textAlign: "center",
    color: colors.grey[600],
  },
  deleteModalContent: {
    paddingVertical: 8,
  },
  deleteModalText: {
    fontSize: 16,
    lineHeight: 24,
    color: colors.grey[700],
  },
  createButton: {
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 8,
  },
  createButtonText: {
    color: "#ffffff",
    fontSize: 14,
    fontWeight: "600",
  },
  formContainer: {
    flex: 1,
    width: "100%",
    maxWidth: 600,
    marginHorizontal: "auto",
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xl,
  },
  formCard: {
    backgroundColor: "#ffffff",
    borderRadius: radii.lg,
    padding: spacing.lg,
    borderWidth: 1,
    borderColor: colors.grey[200],
  },
  formTitle: {
    fontSize: typographyScale.textLg.fontSize,
    lineHeight: typographyScale.textLg.lineHeight,
    fontWeight: "600",
    color: colors.grey[900],
    marginBottom: spacing.xxs,
  },
  formSubtitle: {
    fontSize: typographyScale.textSm.fontSize,
    lineHeight: typographyScale.textSm.lineHeight,
    color: colors.grey[500],
    marginBottom: spacing.lg,
  },
  generatingContainer: {
    alignItems: "center",
    paddingVertical: spacing["2xl"],
    gap: spacing.sm,
  },
  generatingTitle: {
    fontSize: typographyScale.textLg.fontSize,
    lineHeight: typographyScale.textLg.lineHeight,
    fontWeight: "600",
    color: colors.grey[900],
    marginTop: spacing.sm,
    textAlign: "center",
  },
  generatingSubtitle: {
    fontSize: typographyScale.textSm.fontSize,
    lineHeight: typographyScale.textSm.lineHeight,
    color: colors.grey[500],
    textAlign: "center",
    maxWidth: 360,
  },
  fieldGroup: {
    marginBottom: spacing.sm,
  },
  fieldLabel: {
    fontSize: typographyScale.textSm.fontSize,
    lineHeight: typographyScale.textSm.lineHeight,
    fontWeight: "500",
    color: colors.grey[700],
    marginBottom: spacing.xxs,
  },
  input: {
    borderWidth: 1,
    borderColor: colors.grey[300],
    borderRadius: radii.md,
    paddingVertical: 10,
    paddingHorizontal: 12,
    fontSize: typographyScale.textSm.fontSize,
    color: colors.grey[900],
    backgroundColor: "#ffffff",
  },
  inputMultiline: {
    minHeight: 72,
    textAlignVertical: "top",
  },
  inputError: {
    borderColor: colors.error[400],
  },
  formErrorText: {
    fontSize: typographyScale.textXs.fontSize,
    color: colors.error[600],
    marginBottom: spacing.sm,
  },
  formActions: {
    flexDirection: "row",
    justifyContent: "flex-end",
    gap: spacing.xs,
    marginTop: spacing.md,
  },
  cancelButton: {
    paddingVertical: 10,
    paddingHorizontal: spacing.sm,
    borderRadius: radii.md,
    borderWidth: 1,
    borderColor: colors.grey[300],
  },
  cancelButtonText: {
    fontSize: typographyScale.textSm.fontSize,
    fontWeight: "500",
    color: colors.grey[700],
  },
  submitButton: {
    paddingVertical: 10,
    paddingHorizontal: spacing.sm,
    borderRadius: radii.md,
    minWidth: 130,
    alignItems: "center",
  },
  submitButtonText: {
    color: "#ffffff",
    fontSize: typographyScale.textSm.fontSize,
    fontWeight: "600",
  },
  buttonDisabled: {
    opacity: 0.6,
  },
});

export default CompanionActivityView;
